import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Edit2, X, Check, Building2, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ProfitTracker = () => {
  const [invoices, setInvoices] = useState([]);
  const [whInvoices, setWhInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const fetchData = async () => {
    try {
      const [invRes, whRes, supRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/invoices/`),
        axios.get(`${BACKEND_URL}/api/warehouse/invoices`),
        axios.get(`${BACKEND_URL}/api/suppliers?status=active`)
      ]);
      setInvoices(invRes.data.map(i => ({ ...i, source: 'vehicle' })));
      setWhInvoices(whRes.data.map(i => ({ ...i, source: 'warehouse' })));
      setSuppliers(supRes.data || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const allInvoices = [...invoices, ...whInvoices].sort((a, b) => {
    const da = new Date(a.invoice_date || a.created_at || 0);
    const db = new Date(b.invoice_date || b.created_at || 0);
    return db - da;
  });

  const getJobDescription = (inv) => {
    // Pull first line item description from invoice
    const items = inv.line_items || inv.items || [];
    if (items.length > 0) {
      const desc = items[0].description || items[0].service_description || '';
      return desc.trim();
    }
    return '';
  };

  const filteredInvoices = allInvoices.filter(inv => {
    const name = (inv.company_name || inv.customer_name || '').toLowerCase();
    const doc = (inv.document_number || '').toLowerCase();
    const supplier = (inv.supplier_name || '').toLowerCase();
    const job = getJobDescription(inv).toLowerCase();
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || name.includes(q) || doc.includes(q) || supplier.includes(q) || job.includes(q);

    let matchDate = true;
    if (dateFrom || dateTo) {
      const d = (inv.invoice_date || inv.created_at || '');
      const dateStr = d ? new Date(d).toISOString().split('T')[0] : '';
      if (dateFrom && dateStr < dateFrom) matchDate = false;
      if (dateTo && dateStr > dateTo) matchDate = false;
    }
    return matchSearch && matchDate;
  });

  const getSuppliers = (inv) => {
    if (Array.isArray(inv.suppliers) && inv.suppliers.length > 0) return inv.suppliers;
    if (inv.supplier_name || inv.supplier_cost) {
      return [{ name: inv.supplier_name || '', cost: inv.supplier_cost || 0, payment_method: inv.supplier_payment_method || '' }];
    }
    return [];
  };

  const getSupplierCost = (inv) => getSuppliers(inv).reduce((sum, s) => sum + (parseFloat(s.cost) || 0), 0);

  const startEdit = (inv) => {
    setEditingId(inv.id);
    const existing = getSuppliers(inv);
    setEditData({
      suppliers: existing.length > 0
        ? existing.map(s => ({ name: s.name || '', cost: s.cost || 0, payment_method: s.payment_method || '' }))
        : [{ name: '', cost: 0, payment_method: '' }]
    });
  };

  const updateSupplierRow = (idx, patch) => {
    setEditData(prev => ({
      suppliers: prev.suppliers.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    }));
  };

  const addSupplierRow = () => {
    setEditData(prev => ({ suppliers: [...prev.suppliers, { name: '', cost: 0, payment_method: '' }] }));
  };

  const removeSupplierRow = (idx) => {
    setEditData(prev => ({ suppliers: prev.suppliers.filter((_, i) => i !== idx) }));
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  const saveEdit = async (inv) => {
    try {
      const url = inv.source === 'warehouse'
        ? `${BACKEND_URL}/api/warehouse/invoices/${inv.id}`
        : `${BACKEND_URL}/api/invoices/${inv.id}`;
      const cleaned = (editData.suppliers || [])
        .filter(s => (s.name || '').trim() || parseFloat(s.cost) > 0)
        .map(s => ({ name: s.name, cost: parseFloat(s.cost) || 0, payment_method: s.payment_method || '' }));
      const totalCost = cleaned.reduce((sum, s) => sum + s.cost, 0);
      await axios.put(url, {
        suppliers: cleaned,
        supplier_name: cleaned.map(s => s.name).filter(Boolean).join(', '),
        supplier_cost: totalCost,
        supplier_payment_method: cleaned[0]?.payment_method || ''
      });
      toast.success('Supplier info saved');
      setEditingId(null);
      fetchData();
    } catch {
      toast.error('Failed to save');
    }
  };

  const formatCurrency = (amt) => `$${(amt || 0).toFixed(2)}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const totals = filteredInvoices.reduce((acc, inv) => {
    const cost = getSupplierCost(inv);
    acc.revenue += inv.total_amount || 0;
    acc.cost += cost;
    acc.profit += (inv.total_amount || 0) - cost;
    return acc;
  }, { revenue: 0, cost: 0, profit: 0 });

  // Excel-style cell classes
  const thCls = "border border-red-700 px-3 py-2 text-red-700 font-bold uppercase tracking-wide text-xs";
  const tdCls = "border border-yellow-400 px-3 py-2";

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="profit-tracker-title">Profit Tracker</h1>
            <p className="text-gray-600 mt-1">Track supplier costs and profit per job</p>
          </div>
          <Link to="/admin/suppliers">
            <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="manage-suppliers-btn">
              <Building2 size={16} className="mr-2" /> Manage Suppliers
            </Button>
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900" data-testid="total-revenue">{formatCurrency(totals.revenue)}</p>
                </div>
                <DollarSign className="text-blue-600" size={28} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Supplier Cost</p>
                  <p className="text-2xl font-bold text-red-600" data-testid="total-cost">{formatCurrency(totals.cost)}</p>
                </div>
                <DollarSign className="text-red-600" size={28} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Profit</p>
                  <p className={`text-2xl font-bold ${totals.profit >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="total-profit">{formatCurrency(totals.profit)}</p>
                </div>
                <TrendingUp className={totals.profit >= 0 ? 'text-green-600' : 'text-red-600'} size={28} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="py-3">
            <div className="flex flex-wrap gap-3 items-center">
              <Input
                placeholder="Search customer, job, invoice, or supplier..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 min-w-[200px] h-9"
                data-testid="profit-search"
              />
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-36" />
              <span className="text-gray-400 text-sm">to</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-36" />
              {(searchQuery || dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setDateFrom(''); setDateTo(''); }}>
                  <X size={14} className="mr-1" /> Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Excel-style Profit Table */}
        <div className="bg-white rounded-lg shadow border-2 border-yellow-400 overflow-hidden">
          <div className="bg-yellow-300 border-b-2 border-red-700 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-red-700 font-bold text-lg uppercase tracking-wider">Profit Tracker — {filteredInvoices.length} Jobs</h2>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-red-700 font-semibold">Revenue: <span className="font-bold">{formatCurrency(totals.revenue)}</span></span>
              <span className="text-red-700 font-semibold">Cost: <span className="font-bold">{formatCurrency(totals.cost)}</span></span>
              <span className="text-red-700 font-semibold">Profit: <span className={`font-bold ${totals.profit >= 0 ? 'text-green-700' : 'text-red-900'}`}>{formatCurrency(totals.profit)}</span></span>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No invoices found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse" data-testid="profit-table">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-yellow-300">
                    <th className={thCls + " text-left"}>Date</th>
                    <th className={thCls + " text-left"}>Invoice</th>
                    <th className={thCls + " text-left"}>Company / Job</th>
                    <th className={thCls + " text-right"}>Invoice Amount</th>
                    <th className={thCls + " text-left"}>Supplier</th>
                    <th className={thCls + " text-right"}>Supplier Cost</th>
                    <th className={thCls + " text-left"}>Payment</th>
                    <th className={thCls + " text-right"}>Profit</th>
                    <th className={thCls + " text-center w-20"}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((inv, idx) => {
                    const invSuppliers = getSuppliers(inv);
                    const invCost = getSupplierCost(inv);
                    const profit = (inv.total_amount || 0) - invCost;
                    const isEditing = editingId === inv.id;
                    const company = inv.company_name || inv.customer_name || '-';
                    const jobDesc = getJobDescription(inv);
                    const viewLink = inv.source === 'warehouse'
                      ? `/admin/warehouse/invoice/${inv.id}`
                      : `/admin/billing/invoice/${inv.id}`;
                    const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-yellow-50';

                    return (
                      <tr key={inv.id} className={`${rowBg} hover:bg-yellow-100 transition-colors`}>
                        <td className={tdCls + " text-gray-700 whitespace-nowrap"}>{formatDate(inv.invoice_date || inv.created_at)}</td>
                        <td className={tdCls}>
                          <Link to={viewLink} className="text-blue-700 hover:underline font-semibold">{inv.document_number}</Link>
                        </td>
                        <td className={tdCls}>
                          <div className="font-semibold text-gray-900">{company}</div>
                          {jobDesc && <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{jobDesc}</div>}
                        </td>
                        <td className={tdCls + " text-right font-bold text-blue-700"}>{formatCurrency(inv.total_amount)}</td>

                        {isEditing ? (
                          <>
                            <td colSpan="3" className={tdCls + " p-2"}>
                              <div className="space-y-1.5">
                                {(editData.suppliers || []).map((s, sIdx) => (
                                  <div key={sIdx} className="flex items-center gap-1.5">
                                    <select
                                      value={s.name}
                                      onChange={(e) => {
                                        const name = e.target.value;
                                        const sup = suppliers.find(sp => sp.company_name === name);
                                        updateSupplierRow(sIdx, { name, payment_method: sup?.payment_method || s.payment_method || '' });
                                      }}
                                      className="h-8 text-xs border rounded px-2 flex-1 min-w-[130px] bg-white"
                                      data-testid={`edit-supplier-name-${sIdx}`}
                                    >
                                      <option value="">Select supplier...</option>
                                      {s.name && !suppliers.some(sp => sp.company_name === s.name) && (
                                        <option value={s.name}>{s.name}</option>
                                      )}
                                      {suppliers.map(sp => (
                                        <option key={sp.id} value={sp.company_name}>{sp.company_name}</option>
                                      ))}
                                    </select>
                                    <Input type="number" step="0.01" placeholder="Cost" value={s.cost} onChange={(e) => updateSupplierRow(sIdx, { cost: e.target.value })} className="h-8 text-xs w-24 bg-white" data-testid={`edit-supplier-cost-${sIdx}`} />
                                    <select value={s.payment_method} onChange={(e) => updateSupplierRow(sIdx, { payment_method: e.target.value })} className="h-8 text-xs border rounded px-1.5 w-28 bg-white" data-testid={`edit-supplier-payment-${sIdx}`}>
                                      <option value="">Payment...</option>
                                      <option value="Cash">Cash</option>
                                      <option value="Bank Transfer">Bank Transfer</option>
                                      <option value="PayNow">PayNow</option>
                                      <option value="Cheque">Cheque</option>
                                    </select>
                                    {(editData.suppliers || []).length > 1 && (
                                      <button onClick={() => removeSupplierRow(sIdx)} className="text-red-500 hover:text-red-700 shrink-0" title="Remove" data-testid={`remove-supplier-row-${sIdx}`}>
                                        <Trash2 size={14} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <div className="flex items-center justify-between">
                                  <button onClick={addSupplierRow} className="text-xs text-blue-700 font-semibold hover:underline flex items-center gap-1" data-testid="add-supplier-row-btn">
                                    <Plus size={12} /> Add supplier
                                  </button>
                                  {suppliers.length === 0 && (
                                    <Link to="/admin/suppliers" className="text-[10px] text-blue-600 hover:underline">+ Add suppliers first</Link>
                                  )}
                                  <span className="text-xs font-bold text-red-700">
                                    Total: {formatCurrency((editData.suppliers || []).reduce((sum, s) => sum + (parseFloat(s.cost) || 0), 0))}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className={tdCls + " text-right font-bold text-green-700"}>{formatCurrency((inv.total_amount || 0) - (editData.suppliers || []).reduce((sum, s) => sum + (parseFloat(s.cost) || 0), 0))}</td>
                            <td className={tdCls + " text-center p-1"}>
                              <div className="flex gap-1 justify-center">
                                <Button size="sm" className="h-7 w-7 p-0 bg-green-600 hover:bg-green-700" onClick={() => saveEdit(inv)} data-testid="save-supplier-btn"><Check size={14} /></Button>
                                <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={cancelEdit} data-testid="cancel-supplier-btn"><X size={14} /></Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className={tdCls + " text-gray-700"}>
                              {invSuppliers.length === 0 ? (
                                <span className="text-gray-300">-</span>
                              ) : (
                                <div className="space-y-0.5">
                                  {invSuppliers.map((s, sIdx) => (
                                    <div key={sIdx} className="text-xs">
                                      <span className="font-medium">{s.name || '-'}</span>
                                      {invSuppliers.length > 1 && <span className="text-gray-500"> — {formatCurrency(parseFloat(s.cost) || 0)}</span>}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className={tdCls + " text-right text-red-700 font-semibold"}>{invCost ? formatCurrency(invCost) : <span className="text-gray-300">-</span>}</td>
                            <td className={tdCls + " text-gray-700"}>
                              {invSuppliers.filter(s => s.payment_method).length === 0 ? (
                                <span className="text-gray-300">-</span>
                              ) : (
                                <div className="space-y-0.5">
                                  {invSuppliers.map((s, sIdx) => (
                                    <div key={sIdx} className="text-xs">{s.payment_method || '-'}</div>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className={`${tdCls} text-right font-bold ${profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(profit)}</td>
                            <td className={tdCls + " text-center"}>
                              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => startEdit(inv)} title="Edit suppliers" data-testid={`edit-supplier-${inv.id}`}>
                                <Edit2 size={14} />
                              </Button>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-yellow-300 border-t-2 border-red-700 font-bold">
                    <td colSpan="3" className={thCls + " text-right"}>TOTALS:</td>
                    <td className={thCls + " text-right text-blue-700"} data-testid="footer-revenue">{formatCurrency(totals.revenue)}</td>
                    <td className={thCls}></td>
                    <td className={thCls + " text-right"} data-testid="footer-cost">{formatCurrency(totals.cost)}</td>
                    <td className={thCls}></td>
                    <td className={`${thCls} text-right ${totals.profit >= 0 ? 'text-green-700' : 'text-red-900'}`} data-testid="footer-profit">{formatCurrency(totals.profit)}</td>
                    <td className={thCls}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
